import React, { useEffect, useMemo, useRef, useState } from 'react';
import './monitor-control-ui.css';
import PortraitImage from './assets/image/wook.jpeg';
import ParkingMeterBroker from './assets/projectAsset/parkingmeterbroker.jpeg';
import R2 from './assets/projectAsset/r2.png';

const projects = [
  {
    id: 'ai-mobile-app',
    title: 'AI-Powered Intelligent Mobile App (B2C)',
    category: '2025 - Present',
    image: R2,
    summary: 'AI-native mobile product built around Gemini API and AI-OCR workflows for real-time image-to-data structuring.',
    impact: [
      'Published to Google Play and acquired 1,000+ registered users within three months of launch.',
      'Architected an automated AI pipeline that improved image-to-data processing speed by 10x.',
      'Built the product around Next.js, Gemini API, and AI-OCR integrations.'
    ],
    stack: ['Next.js', 'Gemini API', 'AI-OCR', 'Google Play'],
    link: null
  },
  {
    id: 'parking-meter',
    title: 'Vancouver Parking Meter Broker',
    category: 'Personal Project',
    image: ParkingMeterBroker,
    summary: 'Real-time city parking visualization product built with React and the City of Vancouver API.',
    impact: [
      'Won 1st place at the Vancouver Hackathon.',
      'Focused on making city-wide parking data easier to interpret in real time.',
      'Showcased a strong mix of public data UX and rapid product delivery.'
    ],
    stack: ['React.js', 'City of Vancouver API'],
    link: 'https://play.google.com/store/apps/details?id=com.brokers.parkingmeter'
  }
];

const experiences = [
  {
    company: 'Edukiddo',
    role: 'Frontend Developer',
    period: 'May 2025 - Present',
    location: 'Vancouver, BC',
    details: [
      'Implemented an AI-native development workflow, using LLM-assisted engineering to reduce feature delivery time by 40%.',
      'Engineered a Chrome extension for automated government filing, reducing task duration from 60 minutes to 5 minutes with a 0% human error rate.',
      'Architected a scalable B2B CRM and admin portal in React 19, improving large-dataset rendering by 40% with TanStack Table.',
      'Integrated WebSocket using STOMP for real-time synchronization and more accurate operational monitoring.'
    ]
  },
  {
    company: 'Ztax',
    role: 'Freelance Full Stack Developer',
    period: 'Dec 2024 - Apr 2025',
    location: 'Remote',
    details: [
      'Developed an end-to-end tax management platform.',
      'Handled UX and UI design while shaping the database architecture with PHP.'
    ]
  },
  {
    company: 'TmaxAI',
    role: 'Frontend Software Developer',
    period: 'Feb 2021 - Aug 2023',
    location: 'Seongnam, South Korea',
    details: [
      'Led frontend engineering for an educational SaaS platform that scaled to 3,000+ MAU.',
      'Developed real-time interactive features that drove a 50% increase in student engagement metrics.'
    ]
  },
  {
    company: 'TmaxAI',
    role: 'Software Engineering Intern',
    period: 'Oct 2020 - Feb 2021',
    location: 'Seongnam, South Korea',
    details: [
      'Built a Python-based data labeling tool for 130+ users, improving labeling efficiency by 30%.',
      'Selected for full-time conversion based on technical impact and ownership.'
    ]
  }
];

const skillGroups = [
  {
    title: 'AI and Automation',
    items: ['Gemini API Integration', 'GPT API Integration', 'LLM Data Normalization', 'AI-OCR', 'AI-Driven Code Verification']
  },
  {
    title: 'Languages and Frontend',
    items: ['JavaScript (ES6+)', 'TypeScript', 'HTML5', 'CSS3', 'React 19', 'Next.js', 'Tailwind CSS', 'Vite']
  },
  {
    title: 'Backend and Delivery',
    items: ['Python', 'PHP', 'Java', 'Node.js', 'Express', 'WebSocket (STOMP)', 'Android Studio (WebView/Hybrid)', 'Docker', 'Play Store Deployment']
  }
];

const education = [
  {
    institution: 'Samsung Software Engineering Fellowship',
    period: 'July 2020',
    detail: 'Received Excellence Award (Top 5%) for technical leadership and secure coding excellence.'
  },
  {
    institution: 'Dankook University',
    period: 'Dec 2019',
    detail: 'Bachelor of Engineering in Materials Science and Engineering.'
  }
];

const sectionIds = ['overview', 'projects', 'experience', 'skills', 'education', 'contact'];

const MonitorControlUI = () => {
  const [activeProjectId, setActiveProjectId] = useState(projects[0].id);
  const [activeSection, setActiveSection] = useState(sectionIds[0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const sectionRefs = useRef({});

  const activeProject = useMemo(
    () => projects.find((project) => project.id === activeProjectId) ?? projects[0],
    [activeProjectId]
  );

  useEffect(() => {
    const audio = new Audio('/bg-music.mp3');
    audio.loop = true;
    audio.volume = 0.35;
    audioRef.current = audio;

    return () => {
      audio.pause();
      audioRef.current = null;
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    if (isPlaying) {
      audio.play().catch(() => {
        setIsPlaying(false);
      });
    } else {
      audio.pause();
    }

    return undefined;
  }, [isPlaying]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visible?.target?.id) {
          setActiveSection(visible.target.id);
        }
      },
      {
        rootMargin: '-30% 0px -45% 0px',
        threshold: [0.2, 0.4, 0.65]
      }
    );

    sectionIds.forEach((id) => {
      const element = sectionRefs.current[id];
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId) => {
    sectionRefs.current[sectionId]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  return (
    <div className="portfolio-shell">
      <aside className="portfolio-sidebar">
        <div className="profile-card">
          <div className="profile-image-wrap">
            <img src={PortraitImage} alt="Jeongwook Kim" className="profile-image" />
          </div>
          <span className="eyebrow">Full Stack / Frontend Developer</span>
          <h1>Jeongwook Kim</h1>
          <p className="profile-summary">
            AI-driven product specialist focused on AI-native workflows, high-performance web apps, and scalable B2B or B2C delivery.
          </p>
          <div className="status-pill">Based in Vancouver, building AI-native and operationally efficient products</div>
        </div>

        <nav className="section-nav" aria-label="Portfolio sections">
          {sectionIds.map((sectionId) => (
            <button
              key={sectionId}
              type="button"
              className={`section-link ${activeSection === sectionId ? 'active' : ''}`}
              onClick={() => scrollToSection(sectionId)}
            >
              {sectionId}
            </button>
          ))}
        </nav>

        <div className="sidebar-card">
          <div className="sidebar-card-label">Quick Snapshot</div>
          <ul className="snapshot-list">
            <li>LLM-assisted development workflows that reduced delivery time by 40%</li>
            <li>B2B CRM, admin portal, and automation product experience</li>
            <li>Public launches across Google Play and production SaaS environments</li>
          </ul>
        </div>
      </aside>

      <div className="portfolio-content">
        <header className="topbar">
          <div>
            <span className="eyebrow">AI-Native Product Builder</span>
            <div className="topbar-title">Building faster development systems and sharper product experiences with AI in the loop</div>
          </div>
          <div className="topbar-actions">
            <a className="ghost-button" href="mailto:jeongwookkim92@gmail.com">
              Email
            </a>
            <a className="ghost-button" href="https://linkedin.com/in/jeongwookkim" target="_blank" rel="noreferrer">
              LinkedIn
            </a>
            <button
              type="button"
              className={`ghost-button ${isPlaying ? 'playing' : ''}`}
              onClick={() => setIsPlaying((current) => !current)}
            >
              {isPlaying ? 'Music on' : 'Music off'}
            </button>
          </div>
        </header>

        <section
          id="overview"
          className="content-section hero-section"
          ref={(element) => {
            sectionRefs.current.overview = element;
          }}
        >
          <div className="hero-copy">
            <span className="eyebrow">Overview</span>
            <h2>
              I build AI-native workflows and high-performance applications that reduce friction for both teams and users.
            </h2>
            <p className="hero-description">
              Full stack developer specializing in LLM-powered automation, scalable B2B or B2C products,
              and rapid prototyping. My recent work uses Gemini and GPT-based systems to accelerate
              development cycles by 40% while improving operational efficiency.
            </p>
            <div className="hero-actions">
              <button type="button" className="primary-button" onClick={() => scrollToSection('projects')}>
                Explore projects
              </button>
              <button type="button" className="secondary-button" onClick={() => scrollToSection('contact')}>
                Contact me
              </button>
            </div>
          </div>

          <div className="hero-metrics">
            <div className="metric-card">
              <span className="metric-value">40%</span>
              <span className="metric-label">Faster feature delivery through AI-native development workflow</span>
            </div>
            <div className="metric-card">
              <span className="metric-value">92%</span>
              <span className="metric-label">Task time reduction on automated government filing workflow</span>
            </div>
            <div className="metric-card">
              <span className="metric-value">3,000+</span>
              <span className="metric-label">Monthly active users supported on educational SaaS platform</span>
            </div>
          </div>
        </section>

        <section
          id="projects"
          className="content-section"
          ref={(element) => {
            sectionRefs.current.projects = element;
          }}
        >
          <div className="section-heading">
            <span className="eyebrow">Projects</span>
            <h2>Projects backed by resume-level outcomes</h2>
            <p>
              These are the projects explicitly highlighted in the resume, with the strongest outcomes and technologies brought forward.
            </p>
          </div>

          <div className="project-selector" role="tablist" aria-label="Project selector">
            {projects.map((project) => (
              <button
                key={project.id}
                type="button"
                className={`project-pill ${project.id === activeProject.id ? 'active' : ''}`}
                onClick={() => setActiveProjectId(project.id)}
              >
                <span>{project.title}</span>
                <small>{project.category}</small>
              </button>
            ))}
          </div>

          <article className="project-spotlight">
            <div className="project-media">
              <img src={activeProject.image} alt={activeProject.title} />
            </div>
            <div className="project-body">
              <span className="project-category">{activeProject.category}</span>
              <h3>{activeProject.title}</h3>
              <p className="project-summary">{activeProject.summary}</p>
              <ul className="impact-list">
                {activeProject.impact.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <div className="tag-row">
                {activeProject.stack.map((tech) => (
                  <span key={tech} className="tag">
                    {tech}
                  </span>
                ))}
              </div>
              {activeProject.link ? (
                <a className="text-link" href={activeProject.link} target="_blank" rel="noreferrer">
                  Visit project
                </a>
              ) : (
                <span className="project-note">Resume-listed product without a public link attached here</span>
              )}
            </div>
          </article>
        </section>

        <section
          id="experience"
          className="content-section"
          ref={(element) => {
            sectionRefs.current.experience = element;
          }}
        >
          <div className="section-heading">
            <span className="eyebrow">Experience</span>
            <h2>Experience with measurable operational impact</h2>
          </div>
          <div className="timeline">
            {experiences.map((experience) => (
              <article key={`${experience.company}-${experience.period}`} className="timeline-card">
                <div className="timeline-meta">
                  <span>{experience.period}</span>
                  <span>{experience.location}</span>
                </div>
                <h3>{experience.role}</h3>
                <div className="timeline-company">{experience.company}</div>
                <ul className="impact-list">
                  {experience.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section
          id="skills"
          className="content-section"
          ref={(element) => {
            sectionRefs.current.skills = element;
          }}
        >
          <div className="section-heading">
            <span className="eyebrow">Skills</span>
            <h2>Technical strengths from the resume</h2>
          </div>
          <div className="skill-groups">
            {skillGroups.map((group) => (
              <article key={group.title} className="skill-group-card">
                <h3>{group.title}</h3>
                <div className="tag-row">
                  {group.items.map((item) => (
                    <span key={item} className="tag">
                      {item}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section
          id="education"
          className="content-section"
          ref={(element) => {
            sectionRefs.current.education = element;
          }}
        >
          <div className="section-heading">
            <span className="eyebrow">Education</span>
            <h2>Academic background and fellowship recognition</h2>
          </div>
          <div className="timeline">
            {education.map((item) => (
              <article key={item.institution} className="timeline-card">
                <div className="timeline-meta">
                  <span>{item.period}</span>
                </div>
                <h3>{item.institution}</h3>
                <p className="project-summary">{item.detail}</p>
              </article>
            ))}
          </div>
        </section>

        <section
          id="contact"
          className="content-section contact-section"
          ref={(element) => {
            sectionRefs.current.contact = element;
          }}
        >
          <div className="contact-card">
            <div>
              <span className="eyebrow">Contact</span>
              <h2>Looking for someone who can ship product UI and AI automation together?</h2>
              <p>
                I am most effective on teams that want practical AI integration, fast product iteration,
                and frontend systems that hold up under real usage.
              </p>
            </div>
            <div className="contact-actions">
              <a className="primary-button" href="mailto:jeongwookkim92@gmail.com">
                jeongwookkim92@gmail.com
              </a>
              <div className="contact-meta">+1 672-399-3537</div>
              <a className="text-link" href="https://linkedin.com/in/jeongwookkim" target="_blank" rel="noreferrer">
                linkedin.com/in/jeongwookkim
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default MonitorControlUI;
